import React, { useEffect, useState } from 'react';
import './styles.scss';

interface TimeNotifierProps {
    pontos: any[];
    onOpenModal: () => void;
}

const TimeNotifier: React.FC<TimeNotifierProps> = ({ pontos, onOpenModal }) => {
    const [currentAlert, setCurrentAlert] = useState<{ type: string, title: string, message: string, key: string } | null>(null);

    // Horários padrão da jornada (idealmente viria da API Expediente/Horario)
    const schedule = ["08:00", "12:00", "13:30", "18:00"];
    const scheduleTypes = ["Entrada", "Início Intervalo", "Término Intervalo", "Saída"];

    useEffect(() => {
        const checkTimes = () => {
            if (currentAlert) return; // Wait until user answers current alert

            const now = new Date();
            const todayStr = now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            
            // Filter today's points
            const todayPontos = pontos.filter(p => p.data === todayStr);

            // Fetch ignored/snoozed from localStorage
            const ignoredData = JSON.parse(localStorage.getItem(`ignored_alerts_${todayStr}`) || "{}");

            // 1. Check Normal Schedule
            for (let i = 0; i < schedule.length; i++) {
                const timeStr = schedule[i];
                const typeStr = scheduleTypes[i];
                
                // Parse scheduled time
                const [h, m] = timeStr.split(':').map(Number);
                const scheduledTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);
                
                // Alert happens 30 mins after scheduled time
                const alertTime = new Date(scheduledTime.getTime() + 30 * 60000);

                if (now >= alertTime) {
                    // Check if they registered this type or if they have enough records
                    // Since the user might just have raw records without the 'tipo' string saved correctly in older records, 
                    // we can check if they have at least i+1 records. But ideally we check by `tipo`.
                    const hasPunched = todayPontos.some(p => p.tipo === typeStr || p.observacao === typeStr) || todayPontos.length > i;

                    if (!hasPunched) {
                        const alertKey = `schedule_${i}`;
                        
                        // Check if snooze is still active or ignored
                        if (ignoredData[alertKey] === 'ignored') continue;
                        if (ignoredData[alertKey] && now.getTime() < ignoredData[alertKey]) continue; // snoozed

                        setCurrentAlert({
                            type: alertKey,
                            title: `Aviso: Ponto de ${typeStr}`,
                            message: `Já passou mais de 30 minutos do seu horário de ${typeStr} (${timeStr}) e não identificamos o seu registro.`,
                            key: alertKey
                        });
                        return;
                    }
                }
            }

            // 2. Check Extra Time
            const extraEntrada = todayPontos.find(p => p.tipo === 'Extra - Entrada' || p.observacao === 'Extra - Entrada');
            const extraSaida = todayPontos.find(p => p.tipo === 'Extra - Saída' || p.observacao === 'Extra - Saída');

            if (extraEntrada && !extraSaida) {
                const [h, m] = extraEntrada.horario.split(':').map(Number);
                const extraTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);
                
                // First alert is 4 hours after Extra - Entrada
                const alertKey = `extra_alert`;
                
                if (ignoredData[alertKey] === 'ignored') return; // Cannot ignore extra entirely usually, but let's allow it if requested
                
                // Next alert time is either 4h after start, or the snooze/repeat time
                const nextAlertTime = ignoredData[alertKey] ? new Date(ignoredData[alertKey]) : new Date(extraTime.getTime() + 4 * 60 * 60000);

                if (now >= nextAlertTime) {
                    setCurrentAlert({
                        type: alertKey,
                        title: `Aviso: Hora Extra Prolongada`,
                        message: `Você iniciou uma Hora Extra há bastante tempo. Você ainda está trabalhando? Se já finalizou, registre a saída.`,
                        key: alertKey
                    });
                    return;
                }
            }
        };

        const interval = setInterval(checkTimes, 30000); // check every 30s
        checkTimes(); // check immediately on mount

        return () => clearInterval(interval);
    }, [pontos, currentAlert]);

    const handleAction = (action: 'register' | 'snooze' | 'ignore') => {
        if (!currentAlert) return;

        const now = new Date();
        const todayStr = now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const ignoredData = JSON.parse(localStorage.getItem(`ignored_alerts_${todayStr}`) || "{}");

        if (action === 'ignore') {
            // Se for hora extra, 'ignorar' na verdade significa lembrar de novo em 2 horas conforme a regra
            if (currentAlert.key === 'extra_alert') {
                ignoredData[currentAlert.key] = now.getTime() + 2 * 60 * 60000; // 2 horas
            } else {
                ignoredData[currentAlert.key] = 'ignored';
            }
        } else if (action === 'snooze') {
            ignoredData[currentAlert.key] = now.getTime() + 15 * 60000; // 15 minutos de soneca
        }

        localStorage.setItem(`ignored_alerts_${todayStr}`, JSON.stringify(ignoredData));

        if (action === 'register') {
            onOpenModal();
        }

        setCurrentAlert(null);
    };

    if (!currentAlert) return null;

    return (
        <div className="notifier-overlay">
            <div className="notifier-modal">
                <div className="notifier-icon">⏰</div>
                <h3>{currentAlert.title}</h3>
                <p>{currentAlert.message}</p>
                <div className="notifier-actions">
                    <button className="btn-register" onClick={() => handleAction('register')}>Registrar Agora</button>
                    <button className="btn-snooze" onClick={() => handleAction('snooze')}>Lembrar mais tarde</button>
                    <button className="btn-ignore" onClick={() => handleAction('ignore')}>Ignorar</button>
                </div>
            </div>
        </div>
    );
};

export default TimeNotifier;
